import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Droplet, Syringe, Activity, Sparkles, Loader2, Download, RotateCcw } from 'lucide-react';

import { API_URL } from '../config';

export default function VoieIntraNasalePage() {
  const { token } = useAuth();
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [formData, setFormData] = useState({ poids: '', medicament: '', dose: '', volume: '' });

  const handleAnalyze = async () => {
    try {
      setIsAnalyzing(true);
      const response = await fetch(`${API_URL}/api/ai/analyze-voie-nasale`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(formData)
      });
      if (!response.ok) throw new Error('Erreur');
      const data = await response.json();
      setAiAnalysis(data);
      toast.success('OK');
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Calculate dose and volume based on weight
  useEffect(() => {
    if (!formData.poids || !formData.medicament) return;
    
    const poids = parseFloat(formData.poids);
    let dose = '';
    let volume = '';
    
    if (formData.medicament === 'sufentanil') {
      const doseCalc = Math.round(poids * 0.6 * 10) / 10; // 0.5-0.7 µg/kg, moyenne 0.6
      dose = `${doseCalc} µg`;
      
      if (poids <= 5) volume = '0.5 ml';
      else if (poids <= 10) volume = '0.5 ml x2';
      else if (poids <= 15) volume = '0.75 ml x2';
      else if (poids <= 20) volume = '0.2 ml';
      else if (poids <= 30) volume = '0.3 ml';
      else if (poids <= 40) volume = '0.4 ml';
      else if (poids <= 50) volume = '0.5 ml';
      else if (poids <= 60) volume = '0.3 ml x2';
      else if (poids <= 70) volume = '0.4 ml x2';
      else if (poids <= 80) volume = '0.5 ml x2';
      else if (poids <= 90) volume = '0.6 ml x2';
      else volume = '0.7 ml x2';
    } else if (formData.medicament === 'ketamine') {
      const doseCalc = Math.round(poids * 0.75 * 10) / 10; // 0.5-1 mg/kg, moyenne 0.75
      dose = `${doseCalc} mg`;
      
      if (poids < 10) volume = '0.1 ml';
      else if (poids <= 20) volume = '0.2 ml';
      else if (poids <= 30) volume = '0.3 ml';
      else if (poids <= 40) volume = '0.4 ml';
      else if (poids <= 50) volume = '0.3 ml x2';
      else if (poids <= 60) volume = '0.4 ml x2';
      else if (poids <= 70) volume = '0.5 ml x2';
      else if (poids <= 80) volume = '0.6 ml x2';
      else if (poids <= 90) volume = '0.7 ml x2';
      else volume = '0.8 ml x2';
    }
    
    setFormData(prev => ({ ...prev, dose, volume }));
  }, [formData.poids, formData.medicament]);

  return (
    <div className="p-3 md:p-6 space-y-3 max-w-5xl mx-auto">
      <div className="flex justify-between items-start gap-2">
        <div className="flex items-center gap-2">
          <Droplet className="w-6 h-6 text-pink-400" />
          <div>
            <h1 className="text-lg md:text-2xl font-bold text-white">Voie Intra-Nasale</h1>
            <p className="text-xs text-gray-400">Dispositif LMA MAD™</p>
          </div>
        </div>
        <Button onClick={handleAnalyze} disabled={isAnalyzing} size="sm">
          {isAnalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
        </Button>
      </div>

      <Tabs defaultValue="medicaments">
        <TabsList className="grid w-full grid-cols-3 bg-gray-800">
          <TabsTrigger value="medicaments">Medicaments</TabsTrigger>
          <TabsTrigger value="indications">Indications</TabsTrigger>
          <TabsTrigger value="technique">Technique</TabsTrigger>
        </TabsList>

        <TabsContent value="medicaments" className="space-y-3 mt-3">
          <Card>
            <CardHeader className="p-3">
              <CardTitle className="text-sm">CALCULATEUR DE DOSE</CardTitle>
            </CardHeader>
            <CardContent className="p-3 space-y-3">
              <Input type="number" placeholder="Poids (kg)" value={formData.poids} onChange={(e) => setFormData({...formData, poids: e.target.value})} />
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setFormData({...formData, medicament: 'sufentanil'})} className={`p-3 rounded text-sm ${formData.medicament === 'sufentanil' ? 'bg-pink-500 text-white' : 'bg-gray-800'}`}>SUFENTANIL</button>
                <button onClick={() => setFormData({...formData, medicament: 'ketamine'})} className={`p-3 rounded text-sm ${formData.medicament === 'ketamine' ? 'bg-purple-500 text-white' : 'bg-gray-800'}`}>KETAMINE</button>
              </div>
              {formData.dose && (
                <Alert className="bg-cyan-500/10 border-cyan-500/30 p-3">
                  <AlertDescription className="space-y-2">
                    <div className="text-sm font-bold text-cyan-300">Dose: {formData.dose}</div>
                    <div className="text-sm font-bold text-cyan-300">Volume: {formData.volume}</div>
                    <div className="text-xs text-gray-400">A diviser entre les deux narines</div>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          <Card className="border-pink-500/30">
            <CardHeader className="p-3">
              <CardTitle className="text-sm text-pink-300">SUFENTANIL - Tableaux posologies</CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <div className="text-xs mb-2 text-gray-400">&lt; 15 kg: Concentration 5 µg/mL</div>
              <table className="w-full text-xs border border-gray-700">
                <thead className="bg-gray-800">
                  <tr><th className="p-1 border border-gray-700">Poids</th><th className="p-1 border border-gray-700">Dose</th><th className="p-1 border border-gray-700">Volume</th></tr>
                </thead>
                <tbody className="text-gray-300">
                  <tr><td className="p-1 border border-gray-700">3-5 kg</td><td className="p-1 border border-gray-700">2.5 µg</td><td className="p-1 border border-gray-700">0.5 ml</td></tr>
                  <tr><td className="p-1 border border-gray-700">6-10 kg</td><td className="p-1 border border-gray-700">5 µg</td><td className="p-1 border border-gray-700">0.5 ml x2</td></tr>
                  <tr><td className="p-1 border border-gray-700">11-15 kg</td><td className="p-1 border border-gray-700">7.5 µg</td><td className="p-1 border border-gray-700">0.75 ml x2</td></tr>
                </tbody>
              </table>
              <div className="text-xs mt-2 mb-2 text-gray-400">&gt; 15 kg: Concentration 50 µg/mL</div>
              <table className="w-full text-xs border border-gray-700">
                <thead className="bg-gray-800">
                  <tr><th className="p-1 border border-gray-700">Poids</th><th className="p-1 border border-gray-700">Dose</th><th className="p-1 border border-gray-700">Volume</th></tr>
                </thead>
                <tbody className="text-gray-300">
                  <tr><td className="p-1 border border-gray-700">16-20 kg</td><td className="p-1 border border-gray-700">10 µg</td><td className="p-1 border border-gray-700">0.2 ml</td></tr>
                  <tr><td className="p-1 border border-gray-700">21-30 kg</td><td className="p-1 border border-gray-700">15 µg</td><td className="p-1 border border-gray-700">0.3 ml</td></tr>
                  <tr><td className="p-1 border border-gray-700">31-40 kg</td><td className="p-1 border border-gray-700">20 µg</td><td className="p-1 border border-gray-700">0.4 ml</td></tr>
                  <tr><td className="p-1 border border-gray-700">41-50 kg</td><td className="p-1 border border-gray-700">25 µg</td><td className="p-1 border border-gray-700">0.5 ml</td></tr>
                  <tr><td className="p-1 border border-gray-700">51-60 kg</td><td className="p-1 border border-gray-700">30 µg</td><td className="p-1 border border-gray-700">0.3 ml x2</td></tr>
                  <tr><td className="p-1 border border-gray-700">61-70 kg</td><td className="p-1 border border-gray-700">40 µg</td><td className="p-1 border border-gray-700">0.4 ml x2</td></tr>
                  <tr><td className="p-1 border border-gray-700">71-80 kg</td><td className="p-1 border border-gray-700">50 µg</td><td className="p-1 border border-gray-700">0.5 ml x2</td></tr>
                  <tr><td className="p-1 border border-gray-700">81-90 kg</td><td className="p-1 border border-gray-700">60 µg</td><td className="p-1 border border-gray-700">0.6 ml x2</td></tr>
                  <tr><td className="p-1 border border-gray-700">91-100 kg</td><td className="p-1 border border-gray-700">70 µg</td><td className="p-1 border border-gray-700">0.7 ml x2</td></tr>
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Card className="border-purple-500/30">
            <CardHeader className="p-3">
              <CardTitle className="text-sm text-purple-300">KETAMINE - Tableaux posologies</CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <div className="text-xs mb-2 text-gray-400">Concentration: 50 mg/mL | Dose: 0.5-1 mg/kg</div>
              <table className="w-full text-xs border border-gray-700">
                <thead className="bg-gray-800">
                  <tr><th className="p-1 border border-gray-700">Poids</th><th className="p-1 border border-gray-700">Dose</th><th className="p-1 border border-gray-700">Volume</th></tr>
                </thead>
                <tbody className="text-gray-300">
                  <tr><td className="p-1 border border-gray-700">&lt;10 kg</td><td className="p-1 border border-gray-700">5 mg</td><td className="p-1 border border-gray-700">0.1 ml</td></tr>
                  <tr><td className="p-1 border border-gray-700">11-20 kg</td><td className="p-1 border border-gray-700">10 mg</td><td className="p-1 border border-gray-700">0.2 ml</td></tr>
                  <tr><td className="p-1 border border-gray-700">21-30 kg</td><td className="p-1 border border-gray-700">15 mg</td><td className="p-1 border border-gray-700">0.3 ml</td></tr>
                  <tr><td className="p-1 border border-gray-700">31-40 kg</td><td className="p-1 border border-gray-700">20 mg</td><td className="p-1 border border-gray-700">0.4 ml</td></tr>
                  <tr><td className="p-1 border border-gray-700">41-50 kg</td><td className="p-1 border border-gray-700">30 mg</td><td className="p-1 border border-gray-700">0.3 ml x2</td></tr>
                  <tr><td className="p-1 border border-gray-700">51-60 kg</td><td className="p-1 border border-gray-700">40 mg</td><td className="p-1 border border-gray-700">0.4 ml x2</td></tr>
                  <tr><td className="p-1 border border-gray-700">61-70 kg</td><td className="p-1 border border-gray-700">50 mg</td><td className="p-1 border border-gray-700">0.5 ml x2</td></tr>
                  <tr><td className="p-1 border border-gray-700">71-80 kg</td><td className="p-1 border border-gray-700">60 mg</td><td className="p-1 border border-gray-700">0.6 ml x2</td></tr>
                  <tr><td className="p-1 border border-gray-700">81-90 kg</td><td className="p-1 border border-gray-700">70 mg</td><td className="p-1 border border-gray-700">0.7 ml x2</td></tr>
                  <tr><td className="p-1 border border-gray-700">91-100 kg</td><td className="p-1 border border-gray-700">80 mg</td><td className="p-1 border border-gray-700">0.8 ml x2</td></tr>
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="indications" className="space-y-3 mt-3">
          <Card>
            <CardHeader className="p-3">
              <CardTitle className="text-sm">INDICATIONS SMUR</CardTitle>
            </CardHeader>
            <CardContent className="p-3 space-y-2 text-xs text-gray-300">
              <div>• Antalgie (douleur moderee a severe)</div>
              <div>• Crise epilepsie (si pas VVP)</div>
              <div>• Sedation legere (procedure courte)</div>
              <div>• Intoxication opiaces (Naloxone)</div>
              <div>• Pre-medication proceduraleAgitation (ketamine sub-dissociative)</div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="technique" className="space-y-3 mt-3">
          <Card>
            <CardHeader className="p-3">
              <CardTitle className="text-sm">TECHNIQUE LMA MAD</CardTitle>
            </CardHeader>
            <CardContent className="p-3 space-y-2 text-xs text-gray-300">
              <div>1. Preparer dose dans seringue</div>
              <div>2. Connecter LMA MAD a la seringue</div>
              <div>3. Patient tete legerement en arriere</div>
              <div>4. Inserer embout dans narine (vers haut et arriere)</div>
              <div>5. Pulveriser MOITIE de la dose</div>
              <div>6. Repeter dans autre narine</div>
              <div>7. Surveiller: depression respiratoire, sedation</div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {aiAnalysis && (
        <Card className="border-purple-500/30 bg-purple-500/5 mt-3">
          <CardHeader className="p-3">
            <CardTitle className="text-sm text-purple-300"><Sparkles className="w-4 h-4 inline mr-2" />ANALYSE IA</CardTitle>
          </CardHeader>
          <CardContent className="p-3 text-xs text-white whitespace-pre-wrap">{aiAnalysis.analysis}</CardContent>
        </Card>
      )}
    </div>
  );
}
